import React, { useState, useEffect } from "react";
import SEO from "../seo";
const Reports = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "what-youll-get", "why-it-matters"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Reports & Analytics | xPage Help Center"
        description="Explore upcoming Reports and Analytics features in xPage. Track store performance, monitor sales trends, analyze customer behavior, and make data-driven decisions for your business."
        keywords="xPage, reports, analytics, store insights, sales reports, marketing analytics, customer data, performance dashboard, data-driven decisions"
        canonical="https://help.xpage.ai/help/business-expansion/Report"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Reports – Coming Soon
          </h1>
          <p className="text-sm text-systemLabel">
            Gain deeper visibility into your store's performance, from traffic
            insights to sales trends, all in one intuitive dashboard.
          </p>
          <p className="text-sm text-systemLabel">
            Xpage is developing a powerful Analytics & Reports suite designed to
            help you understand what's working, identify growth opportunities,
            and make data-driven decisions effortlessly.
          </p>
        </header>

        {/* What You'll Get Section */}
        <section id="what-youll-get" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            What You'll Get (Coming Soon)
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Feature
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      Description
                    </strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Performance Overview
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Monitor your store's key metrics including visits,
                    conversion rates, and revenue in real time.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Sales Insights
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Track product performance, customer behavior, and order
                    trends to optimize your offerings.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Marketing Reports
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Measure the impact of your campaigns, channels, and traffic
                    sources across the customer journey.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Customer Analytics
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Understand who your customers are, what they buy, and how
                    they interact with your store.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Custom Reports
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Build personalized dashboards and export data to match your
                    business needs.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Why It Matters Section */}
        <section id="why-it-matters" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Why It Matters
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Benefit
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      Description
                    </strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Make Informed Decisions
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Use data to guide your marketing, product, and pricing
                    strategies.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Track Growth Effortlessly
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    See what's driving your success — and where to focus next.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Collaborate Smarter
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Share insights with your team and keep everyone aligned with
                    real-time metrics.
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                The Analytics & Reports module is coming soon, giving you the
                clarity and control you need to grow your business confidently.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#what-youll-get"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "what-youll-get"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                What You'll Get
              </a>
            </li>
            <li>
              <a
                href="#why-it-matters"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "why-it-matters"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Why It Matters
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Reports;
